import { useEffect, useMemo, useState } from "react";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import { exportOrderReport, orderReport } from "Api/reposts";
import {
    ORDER_TYPE, 
    PROJECT_NAME, 
    ORDER_DATE, 
    Quantity,
    TOTAL_PRICE, 
    RECEIVED_AMOUNT,
     OUTSTANDING_AMOUNT,
      PAYMENT_STATUS, 
      ORDER_ID,
      getPaymentStatusColor,
      OrderTypeList,
      PaymentStatus
} from '../../Components/constants/Reports';
import { CUSTOMER_NAME, PRODUCT_CODE } from "Components/constants/JobCard";
import { checkStatusCodeSuccess, deleteUploadedFile, notFound } from "Components/constants/Common";
import { RiSearchLine } from "react-icons/ri";
import { BaseSelect } from "Components/Base/BaseSelect";
import { getCustomerList } from "Api/jobcard";
import { getProjectList } from "Api/common";
import { SelectPlaceHolder } from "Components/constants/validation";
import { BaseImageURL } from "Api/services";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import Loader from "Components/Base/BaseLoader";
import moment from "moment";


// Define the types for the order data
interface Products {
    id: number;
    product_code: string;
}

interface OrderItem {
    id: number;
    order_id: number;
    quantity: number;
    Product: Products;
}

interface AuthUser {
    name: string;
}

interface Project {
    project_name: string;
}

interface Order {
    id: number;
    order_type: string;
    authUser: AuthUser;
    project: Project;
    order_date: string;
    total_price: number;
    payment_status: string;
    orderItem: OrderItem[];
    orderHistory: {
        id: number;
        order_id: number;
        received_amount: number;
        out_standing_amount: number;
    }[];
}

interface Pagination {
    totalItems: number;
    totalPages: number;
    page: number;
    pageSize: number;
}

interface ApiResponse {
    statusCode: number;
    orders: Order[];
    pagination: Pagination;
}

const OrderReport: React.FC = () => {
    document.title = "Order Report";

    const [orderData, setOrderData] = useState<Order[]>([]);
    const [loader, setLoader] = useState<boolean>(false);
    const [customPageSize, setCustomPageSize] = useState<number>(10);
    const [page, setpage] = useState(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [columnName, setColumnName] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<string>("asc");
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [error, setError] = useState<boolean>(false);
    //state for filtering
    const [selectedOrderType, setSelectedOrderType] = useState<string | null>(null);
    const [customerName, setCustomerName] = useState([]);
    const [ProjectName, setProjectName] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const handleFetchData = (page: any) => {
        setpage(page);
      };

      const fetchCustomers = async () => {
        try {
            const response = await getCustomerList();
            if (response.data && checkStatusCodeSuccess(response.status)) {
                setCustomerName(response.data);
            } else {
                console.error("Failed to fetch customers:", response);
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
            
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchProductList = async () => {
        try {
            const response = await getProjectList({});
            if (response.data && checkStatusCodeSuccess(response.status)) {
                setProjectName(response.data);
            } else {
                console.error("Failed to fetch customers:", response);
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchProductList();
    }, []);


      const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    
    // Define columns for the table
    const orderColumns = useMemo(() => [
        {
            header: ORDER_ID,
            accessorKey: "id",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>{cell.row.original.order_draft_id || notFound.nullData}</span>;
            },
        },
        {
            header: ORDER_TYPE,
            accessorKey: "order_type",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>{cell.row.original.order_type || notFound.nullData}</span>;
            },
        },
        {
            header: CUSTOMER_NAME,
            accessorKey: "authUser.name",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>{cell.row.original.authUser?.name || notFound.nullData}</span>;
            },
        },
        {
            header: PROJECT_NAME,
            accessorKey: "project.project_name",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                return <span>{cell.row.original.project?.project_name || notFound.nullData}</span>;
            },
        },
        {
            header: ORDER_DATE,
            accessorKey: "order_date",
            cell: (cell: any) => {
                return (
                    <div>
                        <span>
                            {cell.row.original.order_date
                                ? moment(cell.row.original.order_date).format("ll")
                                : notFound.nullData}
                        </span>
                    </div>
                );
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
       
        {
            header: Quantity,
            accessorKey: "orderItem",
            cell: (cell: any) => {
                return <span>{cell.row.original.quantity || notFound.nullData}</span>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            header: TOTAL_PRICE,
            accessorKey: "total_price",
            enableSorting: true,
            enableColumnFilter: false,
            cell: (cell: any) => {
                const value = cell.row.original.total_price;
                return (
                    <div className="list-group bg-none list-group-flush p-1">
                        <span>
                            {value !== null ? (
                                <>
                                    <i className="mdi mdi-currency-rupee"></i>
                                    {value}
                                </>
                            ) : (
                                notFound.nullData
                            )}
                        </span>
                    </div>
                );
            },
        },
        {
            header: RECEIVED_AMOUNT,
            accessorKey: "orderHistory",
            cell: (cell: any) => {
                const receivedAmount = cell.row.original.received_amount;
                return <span><i className="mdi mdi-currency-rupee"></i>{receivedAmount !== null ? receivedAmount : notFound.nullData}</span>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            header: OUTSTANDING_AMOUNT,
            accessorKey: "orderHistory",
            cell: (cell: any) => {
                const outstandingAmount = cell.row.original.out_standing_amount;
                return <span><i className="mdi mdi-currency-rupee"></i>{outstandingAmount !== null ? outstandingAmount: notFound.nullData}</span>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
        {
            header: PAYMENT_STATUS,
            accessorKey: "payment_status",
            cell: (cell: any) => {
                const value = cell.row.original.payment_status;
                const backgroundColor = getPaymentStatusColor(value || notFound.nullData);
                return <span className="badge p-1 rounded text-white" style={{ ...backgroundColor }}>{value || notFound.nullData}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
    ], []);
    
    

    // Function to fetch order data from API
    const fetchOrderData = async () => {
        setError(false);
        setLoader(true);
        const payload = {
            pageSize: customPageSize,
            page: page,
            "sortValue": "asc",
            "sortKey": "id",
            search: searchValue,
            order_type: selectedOrderType,
            customer_name: selectedCustomer,
            payment_status:selectedPaymentStatus,
            project_name:selectedProject
        };

       

        orderReport(payload)
            .then((response) => {
                if (response.data && checkStatusCodeSuccess(response.status)) {
                    const data = response.data.data;
                    setOrderData(data.orders || []);
                    setTotalRecords(data.pagination.totalItems);
                    setTotalPages(data.pagination.totalPages);
                    setCustomPageSize(data.pagination.pageSize);
        
                } else {
                    setError(true);
                    console.error("Failed to fetch orders:", response);
                    
                }
            })
            .catch((error) => {
                setError(true);
                console.error("Error fetching orders:", error);
            })
            .finally(() => {
                setLoader(false);
            });
    };

    useEffect(() => {
        fetchOrderData();
    }, [page, customPageSize, columnName, sortOrder, searchValue,selectedOrderType,selectedCustomer,selectedPaymentStatus,selectedProject]);

   

    const exportFile = async () => {
        setIsExporting(true);  // Start loader when export begins
        const payload = {
            order_type: selectedOrderType,
            customer_name: selectedCustomer,
            payment_status: selectedPaymentStatus,
            project_name: selectedProject,
            // in future need to remove this page size limit
            pageSize: 50 
        };
    
        try {
            const response = await exportOrderReport(payload);
            const fileName = response.data.data;
            const fileUrl = `${BaseImageURL}${fileName}`;
            window.open(fileUrl, '_blank');
            
            // Use success message from backend if available
            toast.success(response.data.message || "File exported successfully!");
            if (fileUrl) {
                deleteUploadedFile(fileName)
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                // Use error message from backend if available
                const errorMessage = error.response?.data?.message || "An unexpected error occurred. Please try again later.";
                
                if (error.response?.status === 404) {
                    toast.info(errorMessage);
                } else {
                    toast.error(errorMessage);
                }
            } else {
                toast.error("An unexpected error occurred. Please try again later.");
            }
        } finally {
            setIsExporting(false);  // Stop loader when export ends
        }
    };
    

    const resetFilters = () => {
        setSelectedOrderType(null);
        setSelectedCustomer(null);
        setSelectedProject(null);
        setSelectedPaymentStatus(null);
        setpage(1); // Reset to the first page
    };
    

    return (
        <div className="page-content">
            <div className="flex-grow-1 mx-3">
                <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
                    <span className="fs-4">Order Report</span>
                    <div className="d-flex align-items-center gap-2">
                    <BaseButton
    className="btn btn-success"
    onClick={exportFile}
    loader={isExporting}  // Pass the loading state here
      // Disable button when exporting
>
    {isExporting ? (
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
    ) : (
        <>
            <i className="mdi mdi-download" /> Export
        </>
    )}
</BaseButton>
                    </div>
                </div>

                <div className="card py-2 px-4 overflow-x-auto">
                <div className="container-fluid">
    <div className="row g-3 align-items-end mb-3 justify-content-end">
        {/* Order Type Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={ORDER_TYPE}
                options={OrderTypeList}
                value={selectedOrderType}
                handleChange={(name: any, value: any) => {
                    setSelectedOrderType(value === "" ? null : value);;
                    setpage(1);
                }}
            />
        </div>

        {/* Payment Status Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={PAYMENT_STATUS}
                options={PaymentStatus}
                value={selectedPaymentStatus}
                className="text-nowrap"
                handleChange={(name: any, value: any) => {
                    setSelectedPaymentStatus(value === "" ? null : value);
                    setpage(1);
                }}
            />
        </div>

        {/* Customer Name Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={CUSTOMER_NAME}
                options={customerName.map((customer: any) => ({
                    label: customer.name,
                    value: customer.name,
                }))}
                value={selectedCustomer}
                handleChange={(name: any, value: any) => {
                    setSelectedCustomer(value);
                    setpage(1);
                }}
            />
        </div>

        {/* Project Name Select */}
        <div className="col-12 col-sm-6 col-lg-2">
            <BaseSelect
                placeholder={SelectPlaceHolder("")}
                label={PROJECT_NAME}
                options={ProjectName.map((project: any) => ({
                    label: project.project_name,
                    value: project.project_name,
                }))}
                value={selectedProject}
                handleChange={(name: any, value: any) => {
                    setSelectedProject(value);
                    setpage(1);
                }}
            />
        </div>

        {/* Reset Button */}
        <div className="col-12 col-sm-6 col-lg-auto ms-lg-2 d-flex justify-content-end"  style={{ minWidth: '100px' }}>
            <BaseButton
                className="btn btn-danger px-4"
                onClick={resetFilters}
               
            >
                <i className="mdi mdi-refresh me-1" />
                Reset
            </BaseButton>
        </div>
    </div>
</div>
                    {(!error && (loader || orderData.length > 0)) ? (
    <TableContainer
        customPageSize={customPageSize}
        setCustomPageSize={setCustomPageSize}
        isGlobalFilter={true}
        columns={orderColumns}
        data={orderData}
        tableClass="table bg-white"
        divClass="text-nowrap"
        manualPagination={true}
        manualFiltering={false}
        tableLoader={loader}
        hasManualPagination={true}
        SearchPlaceholder="Search orders..."
        currentPage={page}
        setCurrentPage={setpage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        totalNumberOfRows={totalRecords}
        fetchData={handleFetchData}
        fetchSortingData={fetchOrderData}
    />
) : (
    <div className="py-4 text-center">
        <div>
            <span className="fs-1 text-success">
                <RiSearchLine />
            </span>
        </div>
        <div className="mt-4">
            <h5>Order Data Not Found</h5>
            <p className="text-muted">The data you are looking for not available.</p>
        </div>
    </div>
                    )
                    
                    }
                </div>
            </div>
        </div>
    );
};



export default OrderReport;